import moment from "moment";

/**
 * Checks whether a draft inspection has all required details filled and saved
 * for direct scheduling without opening the modal wizard.
 * 
 * @param {Object} data - Raw inspection data returned from GET_INSPECTION_DETAILS API
 * @returns {boolean} True if all required scheduling details are present and non-empty.
 */
export const isDraftComplete = (data) => {
  if (!data) return false;

  // 1. Inspectors check: at least 1 inspector assigned
  const hasInspectors = Array.isArray(data.orderinspector) && data.orderinspector.length > 0;

  // 2. Address check: street address non-empty
  const hasAddress = typeof data.location_address === "string" && data.location_address.trim().length > 0;

  // 3. Client check: at least 1 client assigned
  const hasClient = Array.isArray(data.orderclient) && data.orderclient.length > 0;

  // 4. Services check: at least 1 service selected
  const hasServices = Array.isArray(data.orderservice) && data.orderservice.length > 0;

  // 5. Templates check: at least 1 template selected
  const templates = data.ordertemplate || data.ordertemplates;
  const hasTemplates = Array.isArray(templates) && templates.length > 0;

  return Boolean(hasInspectors && hasAddress && hasClient && hasServices && hasTemplates);
};

/**
 * Constructs the payload required by the UPDATE_INSPECTION_DETAILS API to schedule an inspection.
 * Reuses the exact field structure used by the schedule inspection wizard.
 * 
 * @param {Object} data - Raw inspection data from GET_INSPECTION_DETAILS API
 * @returns {Object} API payload with status = 2 (Scheduled)
 */
export const buildSchedulePayload = (data) => {
  if (!data) return {};

  const companyId = sessionStorage.getItem("company_id") || data.company_id;
  const userId = sessionStorage.getItem("user_id");
  const roleId = sessionStorage.getItem("role_id");
  const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const currentDate = new Date();

  const inspectionId = data.id || data.inspectiondetails?.id;
  const startTime = data.start_time || data.inspectiondetails?.start_time;

  const inspectorIds = [...new Set((data.orderinspector || []).map((i) => i.inspector_id || i.id).filter(Boolean))];
  const clientIds = [...new Set((data.orderclient || []).map((c) => c.client_id || c.id).filter(Boolean))];
  const serviceIds = [...new Set((data.orderservice || []).map((s) => s.service_id || s.service?.id || s.id).filter(Boolean))];
  
  const rawTemplates = data.ordertemplate || data.ordertemplates || [];
  const templateIds = [...new Set(rawTemplates.map((t) => t.template_id || t.template?.id || t.id).filter(Boolean))];

  const buyerAgents = (data.orderagent || [])
    .filter((a) => a.agent_type === 1)
    .map((a) => a.agent_id || a.agent?.id)
    .filter(Boolean);

  const sellerAgents = (data.orderagent || [])
    .filter((a) => a.agent_type === 2)
    .map((a) => a.agent_id || a.agent?.id)
    .filter(Boolean);

  const customDiscounts = (data.orderdiscount || []).map((value) => ({
    id: value.id !== undefined ? value.id : null,
    description: value.description,
    discount: value.price ? value.price * -1 : (value.discount || 0),
  }));

  const unsignedAgreements = data.agreements?.unsigned_agreements || [];
  const agreementIds = unsignedAgreements
    .map((agrmnt) => agrmnt?.agreementdetails?.id || agrmnt?.id)
    .filter(Boolean);

  const serviceDurationMinutes = (data.orderservice || []).map((s) => s.service?.minutes || s.minutes || 0);

  return {
    company_id: companyId,
    user_id: userId,
    role_id: roleId,
    inspection_id: inspectionId,
    inspector_id: inspectorIds,
    start_time: startTime,
    timezone: browserTimeZone,
    location_address: data.location_address || "",
    location_address_2: data.location_address_2 || "",
    location_city: data.location_city || "",
    location_state: data.location_state || "",
    inspection_datetime: startTime ? startTime.split(" ")[0] : "",
    location_zip: data.location_zip || "",
    buyer_agent_id: buyerAgents,
    seller_agent_id: sellerAgents,
    local_starttime: startTime,
    client_id: clientIds,
    service_id: serviceIds,
    order_templates: templateIds,
    charges: data.inspection_fees?.total_fee || data.charges || 0,
    service_duration: serviceDurationMinutes,
    order_charges: data.ordercharge || [],
    order_discounts: customDiscounts,
    agreement_id: agreementIds.filter((item, index) => agreementIds.indexOf(item) === index),
    status: 2, // 2 = Scheduled
    local_agreement_send_time: moment(currentDate).format("YYYY-MM-DD HH:mm:ss"),
  };
};
